import React from 'react'
import { Box, Typography } from '@mui/material'
import { advancedColors } from '../../theme'

// `value` is an untyped `JSONObject`, so a label derived from the key is all an unknown field gets.
export const humanizeKey = (key: string): string => {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim()

  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : key
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const formatFieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '—' : value.map((item) => String(item)).join(', ')
  }

  // Plain objects are drawn as nested rows by the caller; this covers anything else.
  return typeof value === 'object' ? JSON.stringify(value) : String(value)
}

// A fixed-width value lines rows up at any depth, like Standard's read-only fields.
export const HistoryFieldRow = ({ label, value, depth = 0 }: { label: string; value: unknown; depth?: number }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, pl: depth * 2 }}>
    <Typography
      variant="body2"
      title={label}
      sx={{
        flex: 1,
        minWidth: 0,
        color: advancedColors.inkMuted,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </Typography>
    <Box
      sx={{ flexShrink: 0, width: 180, border: `1px solid ${advancedColors.line}`, borderRadius: 1, px: 1.5, py: 0.75 }}
    >
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted, textAlign: 'right', wordBreak: 'break-word' }}>
        {formatFieldValue(value)}
      </Typography>
    </Box>
  </Box>
)

type Row =
  | { kind: 'group'; key: string; label: string; depth: number }
  | { kind: 'field'; key: string; label: string; fieldValue: unknown; depth: number }

const flatten = (value: Record<string, unknown>, depth = 0, parentPath = ''): Row[] =>
  Object.entries(value)
    .filter(([key]) => key !== '__version')
    .flatMap(([key, fieldValue]) => {
      const path = parentPath ? `${parentPath}.${key}` : key
      return isPlainObject(fieldValue)
        ? [
            { kind: 'group' as const, key: path, label: humanizeKey(key), depth },
            ...flatten(fieldValue, depth + 1, path),
          ]
        : [{ kind: 'field' as const, key: path, label: humanizeKey(key), fieldValue, depth }]
    })

const HistoryValueTable = ({ value }: { value: Record<string, unknown> | null | undefined }) => {
  const topLevelEntries = Object.entries(value ?? {}).filter(([key]) => key !== '__version')

  if (topLevelEntries.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: advancedColors.inkMuted }}>
        No details recorded.
      </Typography>
    )
  }

  const rows = flatten(value ?? {})

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      {rows.map((row) =>
        row.kind === 'group' ? (
          <Typography
            key={row.key}
            variant="body2"
            sx={{ pl: row.depth * 2, mt: row.depth > 0 ? 1 : 0, fontWeight: 600 }}
          >
            {row.label}
          </Typography>
        ) : (
          <HistoryFieldRow key={row.key} label={row.label} value={row.fieldValue} depth={row.depth} />
        )
      )}
    </Box>
  )
}

export default HistoryValueTable
